// src/components/VisitorCounting.js
import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  TrendingUp,
  CalendarToday,
  People,
  Refresh,
} from "@mui/icons-material";
import DashboardCard from "./DashboardCard";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const VisitorCounting = () => {
  // State for visitor counts
  const [visitorData, setVisitorData] = useState({
    dailyCount: 0,
    yearlyCount: 0,
    totalCount: 0,
    history: [],
    monthlyHistory: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const theme = useTheme();

  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to fetch visitor data from the server
  const fetchVisitorData = async () => {
    try {
      setError(null);
      const response = await fetch(`${apiUrl}/api/visitor`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setVisitorData({
        dailyCount: data.dailyCount || 0,
        yearlyCount: data.yearlyCount || 0,
        totalCount: data.totalCount || 0,
        history: data.history || [],
        monthlyHistory: data.monthlyHistory || []
      });

      console.log("Dashboard data fetched:", data);
    } catch (err) {
      console.error("Error fetching visitor data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl) {
      fetchVisitorData();

      // Refresh the visitor data every 10 seconds
      const interval = setInterval(() => {
        fetchVisitorData();
      }, 10000);

      return () => clearInterval(interval);
    } else {
      setError("API URL not configured");
      setLoading(false);
    }
  }, [apiUrl]);

  // Reset visitor counts by calling the server endpoint
  const handleReset = async () => {
    setResetDialogOpen(false);
    try {
      const response = await fetch(`${apiUrl}/api/visitor/reset`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setVisitorData({
        dailyCount: data.dailyCount || 0,
        yearlyCount: data.yearlyCount || 0,
        totalCount: data.totalCount || 0,
        history: data.history || [],
        monthlyHistory: data.monthlyHistory || []
      });

      console.log("Reset successful:", data);
    } catch (err) {
      console.error("Error resetting visitor data:", err);
      setError("Error resetting visitor counts: " + err.message);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Format month for display (YYYY-MM -> Month Year)
  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Prepare chart data using the fetched history with theme colors
  const chartData = {
    labels: visitorData.history.map((item) => formatDate(item.date)),
    datasets: [
      {
        label: "Daily Visitors",
        data: visitorData.history.map((item) => item.count),
        backgroundColor: theme.palette.mode === "light"
          ? "rgba(26, 35, 115, 0.2)"
          : "rgba(144, 202, 249, 0.2)",
        borderColor: theme.palette.mode === "light"
          ? "rgba(26, 35, 115, 1)"
          : "rgba(144, 202, 249, 1)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: true,
        text: 'Daily Visitor Trend (Last 7 Days)',
        color: theme.palette.text.primary,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: theme.palette.text.secondary,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          }
        },
        grid: {
          color: theme.palette.mode === "light"
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.1)",
        },
      },
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.mode === "light"
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  // Prepare monthly chart data
  const monthlyChartData = {
    labels: visitorData.monthlyHistory.map((item) => formatMonth(item.month)),
    datasets: [
      {
        label: "Monthly Visitors",
        data: visitorData.monthlyHistory.map((item) => item.count),
        backgroundColor: theme.palette.mode === "light"
          ? "rgba(46, 125, 50, 0.2)"
          : "rgba(129, 199, 132, 0.2)",
        borderColor: theme.palette.mode === "light"
          ? "rgba(46, 125, 50, 1)"
          : "rgba(129, 199, 132, 1)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: true,
        text: 'Monthly Visitor Trend (Last 12 Months)',
        color: theme.palette.text.primary,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: theme.palette.text.secondary,
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          }
        },
        grid: {
          color: theme.palette.mode === "light"
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.1)",
        },
      },
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: theme.palette.mode === "light"
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={fetchVisitorData}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error Loading Visitor Data</AlertTitle>
        {error}
      </Alert>
    );
  }

  const statCards = [
    {
      title: "Today's Visitors",
      value: visitorData.dailyCount,
      icon: <CalendarToday fontSize="large" />,
      color: theme.palette.primary.main,
    },
    {
      title: "This Year",
      value: visitorData.yearlyCount,
      icon: <TrendingUp fontSize="large" />,
      color: theme.palette.success.main,
    },
    {
      title: "All Time",
      value: visitorData.totalCount,
      icon: <People fontSize="large" />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {stat.value.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Daily Chart */}
      <DashboardCard
        title="Daily Visitor Trend"
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchVisitorData}
          >
            Refresh
          </Button>
        }
      >
        {visitorData.history && visitorData.history.length > 0 ? (
          <Box sx={{ height: 400, mt: 2 }}>
            <Line data={chartData} options={chartOptions} />
          </Box>
        ) : (
          <Box
            sx={{
              p: 5,
              textAlign: 'center',
              backgroundColor: theme.palette.mode === "light" ? '#f9f9f9' : '#2a2a2a',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body1" gutterBottom>
              No historical data available for chart display
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Data will appear as visitors are tracked over multiple days
            </Typography>
          </Box>
        )}
      </DashboardCard>

      {/* Monthly Chart */}
      <Box sx={{ mt: 3 }}>
        <DashboardCard title="Monthly Visitor Trend">
          {visitorData.monthlyHistory && visitorData.monthlyHistory.length > 0 ? (
            <Box sx={{ height: 400, mt: 2 }}>
              <Line data={monthlyChartData} options={monthlyChartOptions} />
            </Box>
          ) : (
            <Box
              sx={{
                p: 5,
                textAlign: 'center',
                backgroundColor: theme.palette.mode === "light" ? '#f9f9f9' : '#2a2a2a',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body1" gutterBottom>
                No monthly data available for chart display
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Data will appear as visitors are tracked over multiple months
              </Typography>
            </Box>
          )}
        </DashboardCard>
      </Box>

      {/* Reset Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setResetDialogOpen(true)}
        >
          Reset All Visitor Counts
        </Button>
      </Box>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      >
        <DialogTitle>Reset Visitor Counts?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset all visitor counts? This action cannot be undone and will permanently delete all visitor statistics and history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VisitorCounting;
